import { useState, type ReactNode } from "react";
import type { ReviewComment, ReviewParty, ReviewReply, ReviewStatus } from "../../contracts/types";

const PARTIES: ReviewParty[] = ["Employer", "Contractor", "Engineer", "Other"];

function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return "c" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }
}
function now(): string {
  try {
    return new Date().toISOString().slice(0, 16).replace("T", " ");
  } catch {
    return "";
  }
}

const STATUS_LABEL: Record<ReviewStatus, string> = { open: "Open", accepted: "Accepted", resolved: "Resolved" };

export function ReviewPanel({
  comments,
  anchors,
  onAdd,
  onReply,
  onStatus,
  onRemove,
  banner,
}: {
  comments: ReviewComment[];
  anchors: { value: string; label: string }[];
  onAdd: (c: ReviewComment) => void;
  onReply: (commentId: string, reply: ReviewReply) => void;
  onStatus: (commentId: string, status: ReviewStatus) => void;
  onRemove: (commentId: string) => void;
  /** Optional extra content (e.g. share link) shown under the banner. */
  banner?: ReactNode;
}) {
  const [party, setParty] = useState<ReviewParty>("Employer");
  const [author, setAuthor] = useState("");
  const [anchor, setAnchor] = useState(anchors[0]?.value || "");
  const [text, setText] = useState("");
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const open = comments.filter((c) => c.status === "open").length;
  const accepted = comments.filter((c) => c.status === "accepted").length;
  const resolved = comments.filter((c) => c.status === "resolved").length;
  const ready = comments.length > 0 && open === 0;

  function addComment() {
    if (!text.trim()) return;
    const a = anchors.find((x) => x.value === anchor);
    onAdd({ id: uid(), anchor, anchorLabel: a?.label || anchor, party, author: author.trim() || undefined, text: text.trim(), status: "open", replies: [], at: now() });
    setText("");
  }
  function update(id: string, patch: Partial<ReviewComment>) {
    if (patch.status) onStatus(id, patch.status);
  }
  function remove(id: string) {
    onRemove(id);
  }
  function addReply(id: string) {
    if (!replyText.trim()) return;
    onReply(id, { id: uid(), party, author: author.trim() || undefined, text: replyText.trim(), at: now() });
    setReplyText("");
    setReplyFor(null);
  }

  return (
    <div className="review">
      <div className={`review-banner ${ready ? "ready" : ""}`}>
        {comments.length === 0 ? (
          "No comments yet. Add review comments below and share the project file with the other party."
        ) : ready ? (
          <><b>Ready for signing</b> — all {comments.length} comment(s) accepted or resolved.</>
        ) : (
          <><b>{open} open</b> · {accepted} accepted · {resolved} resolved — not ready for signing.</>
        )}
      </div>
      {banner}

      <div className="group">
        <div className="glabel">Add comment</div>
        <div className="row-3">
          <label><span className="lbl">Party</span>
            <select value={party} onChange={(e) => setParty(e.target.value as ReviewParty)}>{PARTIES.map((p) => <option key={p} value={p}>{p}</option>)}</select>
          </label>
          <label><span className="lbl">Author</span><input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Name" /></label>
          <label><span className="lbl">On section</span>
            <select value={anchor} onChange={(e) => setAnchor(e.target.value)}>{anchors.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}</select>
          </label>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Comment or proposed change…" rows={3} />
        <button className="dark" style={{ marginTop: 8 }} onClick={addComment} disabled={!text.trim()}>Add comment</button>
      </div>

      <div className="group">
        <div className="glabel">Discussion ({comments.length})</div>
        {comments.length === 0 && <div className="hint">Comments you add appear here as threads.</div>}
        {comments.map((c) => (
          <div className={`rc rc-${c.status}`} key={c.id}>
            <div className="rc-head">
              <span className={`rc-party p-${c.party.toLowerCase()}`}>{c.party}{c.author ? ` · ${c.author}` : ""}</span>
              <span className="rc-anchor">{c.anchorLabel}</span>
              <span className={`rc-status s-${c.status}`}>{STATUS_LABEL[c.status]}</span>
            </div>
            <div className="rc-text">{c.text}</div>
            {c.at && <div className="rc-at">{c.at}</div>}

            {c.replies.map((r) => (
              <div className="rc-reply" key={r.id}>
                <span className={`rc-party p-${r.party.toLowerCase()}`}>{r.party}{r.author ? ` · ${r.author}` : ""}</span>
                <span className="rc-rtext">{r.text}</span>
              </div>
            ))}

            <div className="rc-actions">
              {c.status !== "accepted" && <button className="mini" onClick={() => update(c.id, { status: "accepted" })}>✓ Accept</button>}
              {c.status !== "resolved" && <button className="mini" onClick={() => update(c.id, { status: "resolved" })}>Resolve</button>}
              {c.status !== "open" && <button className="mini" onClick={() => update(c.id, { status: "open" })}>Reopen</button>}
              <button className="mini" onClick={() => { setReplyFor(replyFor === c.id ? null : c.id); setReplyText(""); }}>Reply</button>
              <button className="mini" onClick={() => remove(c.id)}>Delete</button>
            </div>
            {replyFor === c.id && (
              <div className="rc-replybox">
                <input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={`Reply as ${party}…`} onKeyDown={(e) => { if (e.key === "Enter") addReply(c.id); }} />
                <button className="dark mini" onClick={() => addReply(c.id)}>Send</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="hint">Comments are saved with the project and travel in Export/Import — share the project file with the other party to collect their responses, then Accept/Resolve here before signing.</div>
    </div>
  );
}
